/**
 * RoadmapItemLikesContent
 *
 * Body of the owner-only RoadmapItemLikesBottomSheet: a like/dislike-count
 * title, a 3-column avatar grid of every user who liked (or disliked) the
 * roadmap item (newest-first), a "No likes yet" / "No dislikes yet" empty
 * state, and load-more pagination.
 *
 * Ports Flutter's `RoadmapItemLikesBottomSheet` body
 * (`lib/presentation/bottom_sheet/roadmap_item_likes_bottom_sheet.dart:248-349`).
 * The `likedType` flag selects the like-vs-dislike copy AND data branch. The
 * manual `GridWidget` becomes the shared `VirtualList` grid mode; the
 * drag-to-dismiss gesture is replaced by the `SheetService` close affordance
 * (allowed web UI deviation).
 */

import React, { useCallback, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { UserAvatarGridItem } from '@/presentation/components/common/user/UserAvatarGridItem';
import { Navigator } from '@/presentation/routing/Navigator';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { useGridColumnsCount } from '@/presentation/hooks';
import { MathUtils } from '@/domain/utils/mathUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { ContentLikedUserEntity } from '@/domain/entities/contentReaction/contentLikedUserEntity';
import { useRoadmapItemLikedUsers } from './hooks/useRoadmapItemLikedUsers';

export interface RoadmapItemLikesContentProps {
  roadmapItemId: string;
  /** `true` → users who liked; `false` → users who disliked. */
  likedType: boolean;
  likes: number;
}

export const RoadmapItemLikesContent: React.FC<RoadmapItemLikesContentProps> = ({
  roadmapItemId,
  likedType,
  likes,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const gridColumnsCount = useGridColumnsCount();

  const { likedUsers, hasMore, isFetching, isFirstLoad, loadMore } =
    useRoadmapItemLikedUsers({ roadmapItemId, likedType });

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Ports `_appBarTitle()` (roadmap_item_likes_bottom_sheet.dart:314-332):
  // 0 → "0 likes"/"0 dislikes", 1 → "1 like"/"1 dislike", else compact count +
  // " likes"/" dislikes". The `likedType` flag picks the like-vs-dislike copy.
  const title =
    likes === 0
      ? likedType
        ? l10n.roadmapItemLikes.zeroLikes
        : l10n.roadmapItemLikes.zeroDislikes
      : likes === 1
        ? likedType
          ? l10n.roadmapItemLikes.oneLike
          : l10n.roadmapItemLikes.oneDislike
        : (likedType
            ? l10n.roadmapItemLikes.manyLikes
            : l10n.roadmapItemLikes.manyDislikes
          ).replace('{count}', MathUtils.getReactionsIntlNumber(likes));

  const handleItemClick = useCallback((userId: string) => {
    // Ports `_itemTapped` → `navigateToUserDetailsScreen` (lines 88-91).
    Navigator.navigateToUserDetails(userId);
  }, []);

  const renderItem = useCallback(
    (row: ContentLikedUserEntity) => (
      <UserAvatarGridItem
        userId={row.userId}
        name={row.username}
        onClick={handleItemClick}
        qa={{ id: `roadmap-item-liked-user-${row.userId}` }}
      />
    ),
    [handleItemClick],
  );

  const showEmptyState = !isFirstLoad && likedUsers.length === 0;

  // Empty-state copy ports `_emptyListWidget` (lines 334-349).
  const emptyStateText = likedType
    ? l10n.roadmapItemLikes.noLikesYet
    : l10n.roadmapItemLikes.noDislikesYet;

  // Coarse lifecycle token reusing existing state: first load with no rows is
  // `loading`; settled with no rows is `empty`; a non-first page in flight is
  // `loading-more`; otherwise `ready`.
  const sheetStatus = isFirstLoad
    ? 'loading'
    : likedUsers.length === 0
      ? 'empty'
      : isFetching
        ? 'loading-more'
        : 'ready';

  return (
    <div
      {...qaAttr({ id: 'roadmap-item-likes-sheet', status: sheetStatus })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div
        {...qaAttr({ id: 'roadmap-item-likes-title', value: title })}
        style={{
          padding: responsive.spacing.lg,
          borderBottom: `${responsive.border.thin}px solid ${theme.border}`,
          textAlign: 'center',
          fontWeight: responsive.fontWeight.bold,
          color: theme.text.primary,
          fontSize: responsive.font.lg,
        }}
      >
        {title}
      </div>

      <div
        ref={scrollRef}
        className="scrollbar-hide"
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: responsive.spacing.base,
          paddingBottom: responsive.spacing.base,
        }}
      >
        {isFirstLoad && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: responsive.spacing.xl,
            }}
          >
            <ExpauseLoadingIndicator />
          </div>
        )}

        {showEmptyState && (
          <div
            style={{
              textAlign: 'center',
              paddingTop: responsive.spacing['3xl'],
              paddingBottom: responsive.spacing['3xl'],
              paddingLeft: responsive.spacing.lg,
              paddingRight: responsive.spacing.lg,
              color: theme.text.secondary,
              fontWeight: responsive.fontWeight.bold,
            }}
          >
            {emptyStateText}
          </div>
        )}

        {likedUsers.length > 0 && (
          <VirtualList
            items={likedUsers}
            renderItem={renderItem}
            getItemKey={(row) => row.userId}
            qa={{ id: 'roadmap-item-likes-grid' }}
            scrollContainer={scrollRef}
            gridProps={{
              // Resolved centrally: inside this sheet surface the hook returns the
              // fixed 3-column count matching Flutter's `gridThreeColumnsCount`.
              columnsCount: gridColumnsCount,
              crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
              mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
              itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
              bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
            }}
            {...(hasMore ? { onReachedEnd: loadMore, reachedEndThreshold: 200 } : {})}
          />
        )}

        {!isFirstLoad && isFetching && hasMore && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: responsive.spacing.lg,
              minHeight: responsive.spacing['3xl'],
            }}
          >
            <ExpauseLoadingIndicator />
          </div>
        )}
      </div>
    </div>
  );
};
